/**
 * Copyright 2025 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */

import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Drawer, Message, Popover } from '@arco-design/web-react';
import { IconSettings } from '@arco-design/web-react/icon';
import ResizeWrapper from '@/components/ResizeWrapper';
import Menu from './Menu';
import { useIsMobile } from '@/utils/utils';
import Apis from '@/app/index';
import MainArea from './MainArea';
import { ABORT_VISIBILITY_CHANGE, useLeave } from '@/lib/useCommon';
import {
  RTCConfig,
  SceneConfig,
  updateRTCConfig,
  updateScene,
  updateSceneConfig,
} from '@/store/slices/room';
import styles from './index.module.less';

export default function () {
  const leaveRoom = useLeave();
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const [showDrawer, setShowDrawer] = useState(false);

  const getScenes = async () => {
    try {
      const {
        scenes,
      }: {
        scenes: {
          rtc: RTCConfig;
          scene: SceneConfig;
        }[];
      } = await Apis.Basic.getScenes();

      if (!Array.isArray(scenes) || !scenes.length) {
        Message.error('未获取到可用场景，请检查服务端场景配置');
        return;
      }

      const availableScenes = scenes.filter((item) => item?.scene?.id && item?.rtc);
      if (!availableScenes.length) {
        Message.error('场景配置不完整，请检查 SceneConfig 和 RTCConfig');
        return;
      }

      dispatch(updateScene(availableScenes[0].scene.id));
      dispatch(
        updateSceneConfig(
          availableScenes.reduce<Record<string, SceneConfig>>((prev, cur) => {
            prev[cur.scene.id] = cur.scene;
            return prev;
          }, {})
        )
      );
      dispatch(
        updateRTCConfig(
          availableScenes.reduce<Record<string, RTCConfig>>((prev, cur) => {
            prev[cur.scene.id] = cur.rtc;
            return prev;
          }, {})
        )
      );
    } catch {
      Message.error('获取场景失败，请检查服务端是否正常启动');
    }
  };

  useEffect(() => {
    getScenes();
    const isOriginalDemo = window.location.host.startsWith('localhost');
    const handler = () => {
      if (
        document.visibilityState === 'hidden' &&
        !sessionStorage.getItem(ABORT_VISIBILITY_CHANGE)
      ) {
        leaveRoom();
      }
    };
    !isOriginalDemo && document.addEventListener('visibilitychange', handler);
    return () => {
      !isOriginalDemo && document.removeEventListener('visibilitychange', handler);
    };
  }, []);

  return (
    <ResizeWrapper className={styles.container}>
      <div className={styles.settingsButtonWrapper}>
        {isMobile ? (
          <>
            <Button
              type="secondary"
              shape="circle"
              icon={<IconSettings />}
              className={styles.settingsButton}
              onClick={() => setShowDrawer(true)}
            />
            <Drawer
              title="设置"
              visible={showDrawer}
              onCancel={() => setShowDrawer(false)}
              footer={null}
              width="100%"
              className={styles.mobileSettingsDrawer}
            >
              <Menu />
            </Drawer>
          </>
        ) : (
          <Popover
            trigger="click"
            position="bl"
            content={
              <div className={styles.settingsPopover}>
                <Menu />
              </div>
            }
          >
            <Button
              type="secondary"
              shape="circle"
              icon={<IconSettings />}
              className={styles.settingsButton}
            />
          </Popover>
        )}
      </div>
      <div
        className={styles.main}
        style={{
          padding: isMobile ? '12px' : '24px',
        }}
      >
        <div className={`${styles.mainArea} ${isMobile ? styles.isMobile : ''}`}>
          <MainArea />
        </div>
      </div>
    </ResizeWrapper>
  );
}
